import { Button } from "@blurb/codex-react";
import { createStyles, MantineProvider } from "@mantine/core";
import { GraphQL } from "@rpi/openapi-api";
import { IconV3, showOldNotification as showNotification } from "@rpi/openapi-core";
import React from "react";
import { useOutlet } from "react-router-dom";
import { Pagination } from "../../../components/Pagination";
import { createTableRowMenuColumn, TableRowMenu } from "../../../components/TableRowMenu";
import { RpiTableContainer } from "../../../components/RpiTableContainer";
import { Heading } from "../../../components/Typography";
import { usePageShellStyles } from "../../../components/pageShellStyles";
import { RpiSearchFilter } from "../../../components/search-filter/RpiSearchFilter";
import { uploadedFilesColumns } from "../../../configuration/columns/upload.column";
import { useUploadedFilesFilter } from "../../../configuration/filters/upload.filter";
import { useNavigateRoute } from "../../../hooks/useNavigateRoute";
import { useRpiPagination } from "../../../hooks/usePagination";
import { RpiTable } from "../../../rpi-core/table/RpiTable";
import { rpaTheme } from "../../../theme/rpaTheme";
import { SearchGeneral, SearchInputs, SearchSort } from "../../../utils/graphql.util";
import { Time } from "../../../utils/time.util";
import { filterNullableArray } from "../../../utils/type.util";

// Floor at 800px so controls + table scroll together and the Upload files CTA never wraps.
const useStyles = createStyles(() => ({
  scrollFloor: { minWidth: 800 },
}));

export const UploadsView: React.FC = () => {
  const { classes: shellClasses } = usePageShellStyles();
  const { classes } = useStyles();
  const navigateRoute = useNavigateRoute();
  const outlet = useOutlet();

  const [sortInput, setSortInput] = React.useState<SearchSort<GraphQL.UploadedFilesQueryVariables>>();
  const [searchInputs, setSearchInputs] = React.useState<SearchInputs<GraphQL.UploadedFilesQueryVariables>>([]);
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.UploadedFilesQueryVariables>>();

  const pagination = useRpiPagination({ perPage: 20 });

  const uploadFilters = useUploadedFilesFilter();

  const { data, isLoading, error, refetch } = GraphQL.useUploadedFilesQuery(
    {
      limit: pagination.perPage,
      offset: pagination.offset,
      search: {
        inputs: searchInputs,
        sort: sortInput,
        generalInput: searchGeneral,
      },
    },
    {
      onSuccess: (data) => pagination.setTotalCount(data.uploadedFiles.totalCount),
      refetchInterval: (data) => {
        if (!data || !data.uploadedFiles?.data) return false;
        const uploadedFiles = filterNullableArray(data.uploadedFiles.data || []);

        if (!uploadedFiles.length) return false;
        const isPendingUpload = uploadedFiles.some((file) => file.status === GraphQL.UploadedFileStatus.Started);

        if (!isPendingUpload) return false;
        return Time.secondsToMilliseconds(5);
      },
    }
  );
  const uploadedFiles = React.useMemo(() => data?.uploadedFiles.data, [data]);

  const deleteMutation = GraphQL.useUploadedFileDeleteMutation<{ message?: string }>({
    onSuccess: () => refetch(),
    onError: (error) => {
      showNotification({
        title: "Failed delete file.",
        message: JSON.stringify(error),
        color: "red",
      });
    },
  });

  React.useEffect(() => {
    if (outlet === null) refetch();
  }, [outlet]);

  return (
    <MantineProvider theme={rpaTheme}>
      <div className={shellClasses.container} data-testid="files-view" aria-label="Files view">
        {outlet}

        <div className={shellClasses.titleRow}>
          <Heading as="h1" size="6xl">
            Files
          </Heading>
        </div>

        <div className={`${shellClasses.scrollStack} ${classes.scrollFloor}`}>
          <div className={shellClasses.row}>
            <div className={shellClasses.searchSlot}>
              <RpiSearchFilter
                placeholder="Search by filename (or collection)"
                filters={uploadFilters}
                onChange={setSearchInputs}
                onSearch={setSearchGeneral}
              />
            </div>
            <div className={shellClasses.titleActions}>
              <Button variant="filled" intent="primary" onClick={() => navigateRoute(["uploads:upload"])}>
                <IconV3 icon="upload" color="inherit" />
                Upload files
              </Button>
            </div>
          </div>

          <RpiTableContainer footer={<Pagination isLoading={isLoading} pagination={pagination} label="Files" />}>
            <RpiTable
              emptyMessage={searchInputs?.length || searchGeneral ? "No files found." : "Your files will appear here."}
              isLoading={isLoading || deleteMutation.isLoading}
              error={error}
              perPage={uploadedFiles?.length}
              data={uploadedFiles}
              columns={uploadedFilesColumns}
              withSortBy={{ onChange: setSortInput }}
              additionalColumns={[
                createTableRowMenuColumn<Pick<GraphQL.UploadedFileResponse, "id" | "url">>((row) => (
                  <TableRowMenu>
                    {row.url && window.open && (
                      <TableRowMenu.Item
                        icon={<IconV3 icon="description" size={20} />}
                        onClick={() => window.open(row.url!, "_blank", "noopener,noreferrer")}
                      >
                        View
                      </TableRowMenu.Item>
                    )}
                    <TableRowMenu.Item
                      icon={<IconV3 icon="delete" size={20} color="inherit" />}
                      onClick={() => row.id && deleteMutation.mutate({ id: row.id })}
                    >
                      Delete
                    </TableRowMenu.Item>
                  </TableRowMenu>
                )),
              ]}
            />
          </RpiTableContainer>
        </div>
      </div>
    </MantineProvider>
  );
};
